/**
 * EnablersSection — Mini-Project Enablers roster on the About tab.
 *
 * Visual treatment matches the LEADERSHIP section on the Geo Community
 * Health tab (CommunityHealthTab.tsx → LeadershipSection): compact
 * horizontal mini-cards in a responsive grid. Avatar on the start
 * side, name on top, uppercase role/topic below, optional subtitle.
 * Hover reveals Lead-only edit / delete overlay buttons.
 *
 * Skip's 2026-05-22 decision (DEV-1080, Chunk B): Enablers moved from
 * Progress tab to About tab. Rationale: Enablers and Team are both
 * "who is part of this" surfaces. About already hosts Team. Progress
 * stays focused on what's moving (Status, Goals, Tasks, BOM).
 *
 * Per POLICY_TEAM_ROSTERS.md 2026-05-20 (migration 170): Enablers are
 * user-only — no role-mode (auto-follow). Template enablers (from
 * Playbook instantiation): cannot delete, cannot change target user
 * identity (delete + re-add to change who). Topic IS editable on
 * templates — only identity is locked.
 *
 * Edit affordances (Lead-only):
 *   - Pencil overlay on each card → inline topic editor.
 *   - × delete overlay on non-template cards.
 *   - Inline "+ Add enabler" button below the row, with L4E user
 *     typeahead pick + optional topic.
 *
 * Strings localized via `pages:miniProject.sections.enablers.*`
 * (Chunk D Phase 2 sweep, 2026-05-22).
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import UserAvatar from "../../../Components/UserAvatar/UserAvatar";
import { useApiSecurityData } from "../../../api/useApiSecurityData";
import { mediaSrc } from "../../../api/mediaUrl";
import {
  createMiniProjectEnablerLink,
  updateMiniProjectEnablerLink,
  deleteMiniProjectEnablerLink,
  type MiniProjectEnablerLink,
} from "../../../api/miniProjects";
import styles from "./sections.module.css";

interface Props {
  miniProjectGuid: string;
  enablerLinks: MiniProjectEnablerLink[];
  isLead: boolean;
  onMutated: () => void;
  /** Add-form open state, lifted to AboutTabPanel so the section
   *  heading "+" button can trigger it (DEV-1081 Chunk D). */
  addOpen?: boolean;
  onCloseAdd?: () => void;
}

interface L4eUser {
  userGuid: string;
  displayName: string | null;
  name: string | null;
  email: string | null;
  screenName: string | null;
  profileImageUrl: string | null;
}

export default function EnablersSection({
  miniProjectGuid,
  enablerLinks,
  isLead,
  onMutated,
  addOpen = false,
  onCloseAdd,
}: Props) {
  const { t } = useAppTranslation("pages");
  // Resolve userGuid → security record (display name + photo).
  // DEV-932: securityUsers is async; the memo recomputes when it lands.
  const { securityUsers } = useApiSecurityData();
  const userByGuid = useMemo(() => {
    const m = new Map<string, L4eUser>();
    for (const u of (securityUsers || []) as any[]) {
      const guid = String(u.user_guid || u.userGuid || "");
      if (!guid) continue;
      m.set(guid.toUpperCase(), {
        userGuid: guid,
        displayName: u.display_name ?? u.displayName ?? null,
        name: u.name ?? null,
        email: u.email ?? null,
        screenName: u.screen_name ?? u.screenName ?? null,
        profileImageUrl: u.profile_image_url ?? u.profileImageUrl ?? null,
      });
    }
    return m;
  }, [securityUsers]);

  // Sort by position (server-set), then by createdAt for stable order.
  const sorted = useMemo(
    () =>
      [...enablerLinks].sort((a, b) => {
        if (a.position !== b.position) return a.position - b.position;
        return a.createdAt.localeCompare(b.createdAt);
      }),
    [enablerLinks],
  );

  if (sorted.length === 0 && !isLead) {
    return <p className={styles.rowMeta}>{t("miniProject.sections.enablers.empty")}</p>;
  }

  return (
    <div>
      {isLead && addOpen && (
        <div style={{ marginBlockEnd: 12 }}>
          <AddEnablerForm
            miniProjectGuid={miniProjectGuid}
            onClose={() => onCloseAdd?.()}
            onMutated={() => {
              onMutated();
              onCloseAdd?.();
            }}
            excludeUserGuids={sorted.map((e) => e.targetUserGuid)}
            userByGuid={userByGuid}
          />
        </div>
      )}
      {sorted.length > 0 ? (
        <div className={styles.leaderGrid}>
          {sorted.map((link) => (
            <EnablerCard
              key={link.enablerLinkGuid}
              miniProjectGuid={miniProjectGuid}
              link={link}
              user={userByGuid.get(link.targetUserGuid.toUpperCase())}
              isLead={isLead}
              onMutated={onMutated}
            />
          ))}
        </div>
      ) : !addOpen ? (
        <p className={styles.rowMeta}>
          {isLead
            ? t("miniProject.sections.enablers.emptyLeadHint")
            : t("miniProject.sections.enablers.empty")}
        </p>
      ) : null}
    </div>
  );
}

/* ── Card ──────────────────────────────────────────────────────── */

function EnablerCard({
  miniProjectGuid,
  link,
  user,
  isLead,
  onMutated,
}: {
  miniProjectGuid: string;
  link: MiniProjectEnablerLink;
  user: L4eUser | undefined;
  isLead: boolean;
  onMutated: () => void;
}) {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const displayName =
    user?.displayName ||
    user?.name ||
    user?.screenName ||
    `${link.targetUserGuid.slice(0, 8)}…`;

  const roleTitle =
    (link.topicText || "").trim() ||
    link.topicKey ||
    t("miniProject.sections.enablers.defaultRole");

  // Template-vs-Lead-added differentiator surfaces on the back face,
  // following the Geo Community Leaders pattern of using backFaceBadge
  // for platform-metadata (not crowding the front face).
  const backFaceBadge = link.isTemplateEnabler ? t("miniProject.sections.enablers.templateBadge") : null;

  // Edit affordance (pencil): allowed on all enablers including
  // templates — only the target user identity is locked on templates,
  // not the topic metadata.
  // Delete affordance (×): Lead-only, non-template only (per migration
  // 170: template cards can't be removed without re-instantiating the
  // Playbook).
  const canEdit = isLead;
  const canDelete = isLead && !link.isTemplateEnabler;

  async function handleDelete() {
    if (!window.confirm(t("miniProject.sections.enablers.confirmDelete", { name: displayName }))) return;
    try {
      await deleteMiniProjectEnablerLink(miniProjectGuid, link.enablerLinkGuid);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // ── Edit mode: replace the card with an inline topic editor ──────
  if (editing) {
    return (
      <EditEnablerForm
        miniProjectGuid={miniProjectGuid}
        link={link}
        displayName={displayName}
        onClose={() => setEditing(false)}
        onMutated={() => {
          onMutated();
          setEditing(false);
        }}
      />
    );
  }

  // Subtitle line: surface the "Template" tag here since the layout
  // doesn't have a back face anymore. Falls back to nothing when this
  // isn't a template enabler — the avatar + name + role stack is enough.
  const subtitle = backFaceBadge;

  // Card body click → L4E profile route. Matches FlipTeamMemberCard's
  // /profile/{userGuid} pattern. Overlay action buttons (edit / delete)
  // stop propagation so they don't trigger navigation while editing.
  const handleCardClick = (e: React.MouseEvent) => {
    // Ignore clicks that originated on a child button — those have
    // their own handlers and stopPropagation, but defensive belt-and-
    // braces in case an overlay child forgets.
    if ((e.target as HTMLElement).closest("button")) return;
    navigate(`/profile/${link.targetUserGuid}`);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/profile/${link.targetUserGuid}`);
    }
  };

  return (
    <div
      className={styles.leaderCard}
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={t("miniProject.sections.enablers.openProfile", { name: displayName })}
      style={{ cursor: "pointer" }}
    >
      <UserAvatar
        userGuid={link.targetUserGuid}
        avatarUrl={user?.profileImageUrl || undefined}
        userName={displayName}
        size="md"
        showPresence={!!user}
      />
      <div className={styles.leaderInfo}>
        <div className={styles.leaderName}>{displayName}</div>
        <div className={styles.leaderRole}>{roleTitle}</div>
        {subtitle && <div className={styles.leaderSubtitle}>{subtitle}</div>}
      </div>
      {canEdit && (
        <button
          type="button"
          aria-label={t("miniProject.sections.enablers.editTopic", { name: displayName })}
          title={t("miniProject.sections.enablers.editTopicTitle")}
          onClick={(e) => {
            e.stopPropagation();
            setError(null);
            setEditing(true);
          }}
          className={`${styles.cardOverlayBtn} ${styles.cardOverlayBtnStart}`}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          aria-label={t("miniProject.sections.enablers.remove", { name: displayName })}
          title={t("miniProject.sections.enablers.removeTitle")}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          className={`${styles.cardOverlayBtn} ${styles.cardOverlayBtnEnd}`}
        >
          ×
        </button>
      )}
      {error && (
        <div className={styles.error} style={{ marginBlockStart: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

/* ── Edit form (inline, replaces a card while editing) ─────────── */

function EditEnablerForm({
  miniProjectGuid,
  link,
  displayName,
  onClose,
  onMutated,
}: {
  miniProjectGuid: string;
  link: MiniProjectEnablerLink;
  displayName: string;
  onClose: () => void;
  onMutated: () => void;
}) {
  const { t } = useAppTranslation("pages");
  const [topicText, setTopicText] = useState(link.topicText || "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await updateMiniProjectEnablerLink(miniProjectGuid, link.enablerLinkGuid, {
        // Server accepts partial updates — only topic is editable from
        // the public surface. Position/topicKey can be added later if
        // there's a reorder UX. Target user identity is immutable.
        topicText: topicText.trim() || null,
      });
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        inlineSize: 220,
        padding: 10,
        background: "var(--bg-elevated)",
        border: "0.5px solid var(--border-default)",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>
        {t("miniProject.sections.enablers.editLabel", { name: displayName })}
      </div>
      <label style={{ fontSize: 11, color: "var(--text-secondary)" }}>
        {t("miniProject.sections.enablers.topicLabel")}
        <input
          value={topicText}
          onChange={(e) => setTopicText(e.target.value)}
          placeholder={t("miniProject.sections.enablers.topicPlaceholder")}
          style={{
            display: "block",
            inlineSize: "100%",
            marginBlockStart: 4,
            padding: "4px 8px",
            fontSize: 12,
            background: "var(--bg-surface)",
            border: "0.5px solid var(--border-default)",
            borderRadius: 4,
            color: "var(--text-primary)",
            boxSizing: "border-box",
          }}
        />
      </label>
      {link.isTemplateEnabler && (
        <div style={{ fontSize: 10, fontStyle: "italic", color: "var(--text-muted)" }}>
          {t("miniProject.sections.enablers.templateLocked")}
        </div>
      )}
      {error && <div className={styles.error}>{error}</div>}
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button
          type="button"
          className={styles.smallButton}
          onClick={onClose}
          disabled={busy}
        >
          {t("miniProject.sections.button.cancel")}
        </button>
        <button
          type="button"
          className={styles.smallButton}
          onClick={save}
          disabled={busy}
        >
          {busy ? t("miniProject.sections.button.saving") : t("miniProject.sections.button.save")}
        </button>
      </div>
    </div>
  );
}

/* ── Add form ──────────────────────────────────────────────────────
 *
 * Two-step flow:
 *   1) Typeahead pick — search any L4E user by display name / name /
 *      email / screenName. Same pattern as MiniProjectTeamTab's
 *      AddMemberTrayBody, kept inline here rather than in a Tray so the
 *      About-tab UX stays simple.
 *   2) Optional topic — once a user is picked, the form shows their
 *      card preview row + a topic input + Save / Back.
 *
 * Excludes already-added enablers from the picker. Empty results until
 * the user starts typing — keeps the dropdown from dumping the whole
 * user catalog on first focus.
 */

function AddEnablerForm({
  miniProjectGuid,
  onClose,
  onMutated,
  excludeUserGuids,
  userByGuid,
}: {
  miniProjectGuid: string;
  onClose: () => void;
  onMutated: () => void;
  /** GUIDs already on the project as enablers — filtered out of the picker. */
  excludeUserGuids: string[];
  /** Snapshot of resolved L4E user records, keyed by uppercase GUID. */
  userByGuid: Map<string, L4eUser>;
}) {
  const { t } = useAppTranslation("pages");
  const [picked, setPicked] = useState<L4eUser | null>(null);
  const [search, setSearch] = useState("");
  const [topicText, setTopicText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Build the full L4E user pool from the resolved snapshot so we
  // can filter it. The userByGuid map is sourced from useApiSecurityData
  // upstream; this keeps the form independent of that hook.
  const allUsers = useMemo<L4eUser[]>(
    () => Array.from(userByGuid.values()),
    [userByGuid],
  );

  const excludeSet = useMemo(
    () => new Set(excludeUserGuids.map((g) => g.toUpperCase())),
    [excludeUserGuids],
  );

  const userOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const pool = allUsers.filter(
      (u) => !excludeSet.has(u.userGuid.toUpperCase()),
    );
    const filtered = pool.filter(
      (u) =>
        (u.displayName ?? "").toLowerCase().includes(q) ||
        (u.name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.screenName ?? "").toLowerCase().includes(q),
    );
    return filtered
      .slice()
      .sort((a, b) =>
        (a.displayName || a.name || a.email || "").localeCompare(
          b.displayName || b.name || b.email || "",
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
      )
      .slice(0, 50);
  }, [allUsers, excludeSet, search]);

  async function submit() {
    if (!picked) {
      setError(t("miniProject.sections.enablers.pickUser"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await createMiniProjectEnablerLink(miniProjectGuid, {
        targetUserGuid: picked.userGuid,
        topicText: topicText.trim() || undefined,
      });
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // ── Step 1: typeahead pick ───────────────────────────────────────
  if (!picked) {
    return (
      <div className={styles.form}>
        <div className={styles.formRow}>
          <label>{t("miniProject.sections.enablers.search")}</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("miniProject.sections.enablers.searchPlaceholder")}
            autoFocus
          />
        </div>
        <div
          style={{
            maxBlockSize: 240,
            overflowY: "auto",
            border: "0.5px solid var(--border-default)",
            borderRadius: 6,
            background: "var(--bg-elevated)",
            marginBlockStart: 4,
          }}
        >
          {userOptions.length === 0 ? (
            <div
              style={{
                padding: "12px 14px",
                fontSize: 12,
                fontStyle: "italic",
                color: "var(--text-muted)",
              }}
            >
              {search.trim()
                ? t("miniProject.sections.enablers.noMatches")
                : t("miniProject.sections.enablers.startTyping")}
            </div>
          ) : (
            userOptions.map((u) => (
              <button
                type="button"
                key={u.userGuid}
                onClick={() => {
                  setPicked(u);
                  setError(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  inlineSize: "100%",
                  padding: "8px 12px",
                  background: "transparent",
                  border: "none",
                  borderBlockEnd:
                    "0.5px solid var(--border-default)",
                  textAlign: "start",
                  cursor: "pointer",
                  color: "var(--text-primary)",
                }}
              >
                <div
                  style={{
                    inlineSize: 28,
                    blockSize: 28,
                    borderRadius: "50%",
                    background: "var(--bg-surface)",
                    color: "var(--text-secondary)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {u.profileImageUrl ? (
                    <img
                      src={mediaSrc(u.profileImageUrl) ?? undefined}
                      alt=""
                      style={{
                        inlineSize: "100%",
                        blockSize: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    (u.displayName || u.name || u.email || "?")
                      .trim()
                      .charAt(0)
                      .toUpperCase()
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {u.displayName ||
                      u.name ||
                      u.screenName ||
                      `${u.userGuid.slice(0, 8)}…`}
                  </div>
                  {u.email && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {u.email}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.smallButton}
            onClick={onClose}
          >
            {t("miniProject.sections.button.cancel")}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: optional topic + confirm ─────────────────────────────
  const pickedLabel =
    picked.displayName ||
    picked.name ||
    picked.screenName ||
    `${picked.userGuid.slice(0, 8)}…`;

  return (
    <div className={styles.form}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "6px 10px",
          background: "var(--bg-surface)",
          border: "0.5px solid var(--border-default)",
          borderRadius: 6,
          marginBlockEnd: 8,
        }}
      >
        <div
          style={{
            inlineSize: 28,
            blockSize: 28,
            borderRadius: "50%",
            background: "var(--bg-elevated)",
            color: "var(--text-secondary)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 500,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {picked.profileImageUrl ? (
            <img
              src={mediaSrc(picked.profileImageUrl) ?? undefined}
              alt=""
              style={{
                inlineSize: "100%",
                blockSize: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            pickedLabel.charAt(0).toUpperCase()
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{pickedLabel}</div>
          {picked.email && (
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {picked.email}
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.smallButton}
          onClick={() => setPicked(null)}
        >
          {t("miniProject.sections.enablers.change")}
        </button>
      </div>

      <div className={styles.formRow}>
        <label>{t("miniProject.sections.enablers.topicLabel")}</label>
        <input
          value={topicText}
          onChange={(e) => setTopicText(e.target.value)}
          placeholder={t("miniProject.sections.enablers.topicAddPlaceholder")}
        />
      </div>
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.smallButton}
          onClick={onClose}
        >
          {t("miniProject.sections.button.cancel")}
        </button>
        <button
          type="button"
          className={styles.smallButton}
          onClick={submit}
          disabled={busy}
        >
          {busy ? t("miniProject.sections.button.saving") : t("miniProject.sections.enablers.button.add")}
        </button>
      </div>
    </div>
  );
}
